import {
    Injectable,
    Injector,
    inject
} from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import {
    ConfigurationManagerService,
    EntityManagerService,
    PrinterDocumentTypeId,
} from "src/app/core";

import { DocumentPrinterOptions } from "src/app/shared/model/document-printer.model";

import {
    groupBy,
    keyBy,
    removeFromArray
} from "src/app/shared/utils";

import {
    PaymentMethods,
    paymentMethodTypes,
    Sales,
    SalesItems,
    SalesPayments,
    SalesPriceChanges
} from "tilby-models";

import {
    DocumentPrintersManagerDialogStateService
} from "src/app/dialogs";

import {
    digitalPaymentsManager,
    documentPrinter,
    fiscalUtils
} from "app/ajs-upgraded-providers";

import {
    MathUtils
} from "@tilby/tilby-ui-lib/utilities";

import {
    SaleUtilsService
} from "./sale-utils.service";

import {
    stringToLines
} from "src/app/shared/string-utils";


@Injectable({
    providedIn: 'root'
})
export class SalePaymentService {
    private readonly translateService = inject(TranslateService);
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly digitalPaymentsManagerService = inject(digitalPaymentsManager);
    private readonly documentPrintersManager = inject(DocumentPrintersManagerDialogStateService);
    private readonly injector = inject(Injector);
    private readonly entityManagerService = inject(EntityManagerService);
    private readonly fiscalUtils = inject(fiscalUtils);
    private readonly saleUtils = inject(SaleUtilsService);

    /**
     * Checks if a sale has any payments and all of them are cash payments.
     *
     * @param {Sales} sale - The sale object to check for cash payments.
     * @return {boolean} true if the sale has any payments and all of them are cash payments, false otherwise.
     */
    public isCashPayment(sale: Sales) {
        return !!sale.payments?.length && sale.payments?.every(p => this.fiscalUtils.isCashPayment(p.payment_method_type_id));
    }

    /**
     * Calculates the total amount to pay for a given sales target.
     *
     * @param {Sales} sale - The sales target to calculate the amount to pay.
     * @param {object} [options] - Optional parameters.
     * @param {PaymentMethods} [options.targetPayment] - The payment method to calculate the amount to pay for. Will be used to check if the sale is a cash payment.
     * @return {number} The amount to pay for the sales target.
     */
    public getTotalAmountToPay(sale?: Sales, options?: { targetPayment: PaymentMethods }) {
        if(!sale) {
            return 0;
        }

        let shouldRound = this.fiscalUtils.isPaymentRoundingEnabled(sale);

        if(shouldRound) {
            if(sale.payments?.length) {
                shouldRound = this.isCashPayment(sale) && (!options?.targetPayment || this.fiscalUtils.isCashPayment(options.targetPayment.payment_method_type_id));
            } else {
                shouldRound = !!options?.targetPayment && this.fiscalUtils.isCashPayment(options?.targetPayment?.payment_method_type_id);
            }
        }

        const targetAmount = this.fiscalUtils.isSplitPayment(sale)
            ? (sale.final_net_amount || 0)
            : (sale.final_amount || 0);

        const saleAmount = shouldRound
            ? this.fiscalUtils.get5CentsRoundedAmount(targetAmount)
            : targetAmount;
            
        return saleAmount;
    }

    /**
     * Calculates the amount difference between the total amount to pay and the amount paid for a given sales target.
     *
     * @param {Sales} sale - The sales target to calculate the amount difference.
     * @param {object} options - Optional parameters.
     * @param {PaymentMethods} options.targetPayment - The payment method to calculate the amount difference for. Will be used to check if the sale is a cash payment.
     * @return {number} The amount difference between the total amount to pay and the amount paid.
     */
    private getAmountPaymentsDifference(sale: Sales, options?: { targetPayment: PaymentMethods }) {
        const totalAmount = this.getTotalAmountToPay(sale, options);
        const paidAmount = this.getPaid(sale);

        return MathUtils.round(totalAmount - paidAmount);
    }

    /**
     * Calculates the amount left to pay for a given sales target.
     *
     * @param {Sales} sale - The sales target to calculate the amount left.
     * @param {object} options - Optional parameters.
     * @param {PaymentMethods} options.targetPayment - The payment method to calculate the amount left for. Will be used to check if the sale is a cash payment.
     * @return {number} The amount left to pay for the sales target.
     */
    public getAmountLeftToPay (sale: Sales, options?: { targetPayment: PaymentMethods }) {
        if (this.fiscalUtils.isRefundVoidSale(sale)) {
            return 0;
        }

        return Math.max(0, this.getAmountPaymentsDifference(sale, options));
    }

    /**
     * Calculates and returns the total amount paid for a given sale.
     *
     * @param {Sales} sale - The sale object containing the payments.
     * @return {number} The total amount paid for the sale.
     */
    public getPaid(sale: Sales) {
        return MathUtils.round(sale.payments?.reduce((sum, p) => sum + p.amount, 0) || 0);
    }

    /**
     * Calculates the change to be given back to the customer after a sale.
     *
     * @param {Sales} sale - the sale object containing the final amount and payments
     * @return {number} the amount of change to be given back to the customer, null if the sale is a void/refund
     */
    public getSaleChange(sale: Sales) {
        return Math.max(0, - this.getAmountPaymentsDifference(sale));
    }

    /**
     * Checks if the given sale has any online digital payments.
     *
     * @param {Sales} sale - The sale object to check for online digital payments.
     * @return {boolean} Returns true if the sale has online digital payments, false otherwise.
     */
    public hasOnlineDigitalPayment (sale: Sales) {
        return !!sale.payments?.some(p => 
            this.digitalPaymentsManagerService.isPaymentDigital(p.payment_method_type_id) &&
            this.digitalPaymentsManagerService.isPaymentOnlineOnly(p.payment_method_type_id)
        );
    }

    /**
     * Calculates the rounding price change for a sale if cash rounding is enabled.
     * 
     * @param {Sales} sale - The sale object to calculate rounding for.
     * @returns {SalesPriceChanges[]} An array containing the rounding price change, or an empty array if no rounding is needed.
     */
    private getRoundingPriceChange(sale: Sales): SalesPriceChanges[] {
        const finalAmount = sale.final_amount || 0;

        if (!this.fiscalUtils.isPaymentRoundingEnabled(sale) || finalAmount <= 0) {
            return [];
        }

        const isCashPayment = (sale.payments || []).every((payment) => this.fiscalUtils.isCashPayment(payment.payment_method_type_id));

        if (!isCashPayment) {
            return [];
        }

        const roundedAmount = this.fiscalUtils.get5CentsRoundedAmount(finalAmount);
        const centsToRound = this.fiscalUtils.roundDecimals(roundedAmount - finalAmount);

        if (centsToRound > 0) {
            return [this.saleUtils.getGenericPriceChange(sale, 'surcharge_fix', centsToRound, this.translateService.instant('CASHREGISTER.ROUND_TOTAL.ROUND'))];
        }

        if (centsToRound < 0) {
            const discountableAmount = this.fiscalUtils.getDiscountableAmount(sale);

            if (discountableAmount >= Math.abs(centsToRound)) {
                return [this.saleUtils.getGenericPriceChange(sale, 'discount_fix', Math.abs(centsToRound), this.translateService.instant('CASHREGISTER.ROUND_TOTAL.ROUND'))];
            }
        }

        return [];
    }

    /**
     * Processes the payments for a sale, and executes the digital transactions if needed
     *
     * @param {Sales} sale - The sale object.
     * @param {DocumentPrinterOptions} pDocData - The document printer options. It will be modified in place in case of overrides.
     */
    public async processPayments (sale: Sales, pDocData: DocumentPrinterOptions) {
        const clonedSale = structuredClone(sale);
        let salePayments = clonedSale.payments || [];
        const tipSaleItems: SalesItems[] = [];

        //Check rounding
        const priceChanges = this.getRoundingPriceChange(clonedSale);

        const paymentMethods = await this.entityManagerService.paymentMethods.fetchCollectionOffline();
        const paymentMethodsById = keyBy(paymentMethods, m => m.id);
        const paymentsInSale = [...new Set(salePayments.map(p => p.payment_method_id))];

        //Check if we need to override the target printer
        const overridePayment = Object.values(paymentMethodsById)
            .filter(payment => paymentsInSale.includes(payment.id))
            .find(payment => payment.printer_id || payment.document_type_id);
        
        let overridePDocData;
        let targetPrinter = pDocData.printer;

        if(overridePayment) {
            //Abort transaction if there is an override payment and more than one payment
            if(salePayments.length > 1) {
                throw 'CASHREGISTER.ACTIVE_SALE.MULTIPLE_PAYMENT_OVERRIDE_PRINTERS';
            }

            overridePDocData = await this.documentPrintersManager.getPrinterDocumentData(overridePayment.printer_id || pDocData?.printer.id || 'default', (overridePayment.document_type_id as PrinterDocumentTypeId || pDocData?.document_template?.id || pDocData.document_type.id), (pDocData.options || {}) );

            targetPrinter = overridePDocData.printer;
        }

        const paymentsByMethodType = groupBy(salePayments, p => p.payment_method_type_id);
        const digitalPayments = salePayments.filter((payment) => this.digitalPaymentsManagerService.isPaymentDigital(payment.payment_method_type_id));

        pDocData.tail = "";

        if (digitalPayments.length) {
            const documentPrinterService = this.injector.get(documentPrinter);

            // Check if the printer is reachable if any digital payment is not paid
            if(digitalPayments.some(p => !p.paid)) {
                await documentPrinterService.isPrinterReachable(targetPrinter);
            }

            try {
                const resultTail: string[] = [];

                const addTail = (payment: SalesPayments) => {
                    const paymentMethod = paymentMethodsById[payment.payment_method_id];

                    if(!paymentMethod.print_receipt) {
                        return;
                    }

                    const tail = this.digitalPaymentsManagerService.getPaymentTail(payment);

                    if (tail) {
                        resultTail.push(tail);
                    }
                };

                for (const payment of digitalPayments) {
                    if (!payment.paid) {
                        let paymentResults = await this.digitalPaymentsManagerService.digitalPayment(payment.amount, payment.payment_method_id, { sale: { ...clonedSale, payments: salePayments } });

                        if (!Array.isArray(paymentResults)) {
                            paymentResults = [paymentResults];
                        }

                        //Remove the payment from the sale since it could be split
                        salePayments = salePayments.filter((p) => p !== payment) || [];

                        const now = new Date().toISOString();

                        for (const resultPayment of paymentResults) {
                            //Set the payment as paid if the digital payment cannot be rolled back in case of transaction/print failure
                            resultPayment.amount = resultPayment.amount || payment.amount;
                            //@ts-expect-error
                            resultPayment.date = resultPayment.date || now;

                            addTail(resultPayment);
                            delete resultPayment.tail;
                            
                            if(!resultPayment.is_tip) {
                                resultPayment.paid = this.digitalPaymentsManagerService.paymentHasPrintFailRollback(resultPayment.payment_method_type_id) ? false : true;
                            }

                            salePayments.push(resultPayment);
                        }
                    } else {
                        addTail(payment);
                    }
                }

                for(const payment of salePayments) {
                    if(payment.is_tip && !payment.paid) {
                        const tipSaleItem = await this.saleUtils.getTipSaleItem(payment.amount);
                        tipSaleItems.push(tipSaleItem);
                        payment.paid = true;
                    }
                }

                pDocData.tail = resultTail.join('\n\n');
            } catch (error: any) {
                let errorToReturn = error || 'UNKNOWN_ERROR';

                if (error?.response) {
                    if (error.receiptData) {
                        let printer = pDocData?.printer;

                        if (printer) {
                            documentPrinterService.printFreeNonFiscal(error.receiptData, printer.id, { printHeader: true });
                        }
                    }

                    errorToReturn = error.response;
                }

                return { salePayments, error: errorToReturn };
            }

            const toPay = this.getAmountLeftToPay({
                ...clonedSale,
                payments: salePayments
            });

            if (toPay) {
                return { error: 'CASHREGISTER.ACTIVE_SALE.DIGITAL_PAYMENT_LEFT_TO_PAY', salePayments };
            }
        }

        //Add tail for bank transfer payments (only if any of the preferences is set)
        if(paymentsByMethodType[8]) {
            pDocData.tail = (pDocData.tail || "") + this.getBankTailInfo();
        }

        //If present, align pienissimo giftcard amount
        if(paymentsByMethodType[10]) {
            const pienissimoGiftcard = salePayments.find(payment => payment.acquirer_name === 'pienissimo_giftcard');

            if(pienissimoGiftcard) {
                const toPay = this.getAmountLeftToPay({
                    ...clonedSale,
                    payments: salePayments.filter(payment => payment !== pienissimoGiftcard)
                });

                pienissimoGiftcard.amount = Math.min(toPay, pienissimoGiftcard.amount);
            }
        }

        if(overridePDocData) {
            Object.assign(pDocData, overridePDocData);
        }

        return {
            priceChanges,
            salePayments,
            tipSaleItems
        };
    }

    private getBankTailInfo () {
        const eInvoiceInfo = this.configurationManagerService.getShopPreferences('e_invoice.');
        const bankTailRows = [];

        if (eInvoiceInfo['e_invoice.bank_merchant_name']) {
            bankTailRows.push(eInvoiceInfo['e_invoice.bank_merchant_name']);
        }

        if (eInvoiceInfo['e_invoice.bank_name']) {
            bankTailRows.push(this.translateService.instant('CASHREGISTER.PAYMENTS.BANK_TRANSFER.BANK_NAME', { name: eInvoiceInfo['e_invoice.bank_name'] }));
        }

        if (eInvoiceInfo['e_invoice.bank_iban']) {
            bankTailRows.push(this.translateService.instant('CASHREGISTER.PAYMENTS.BANK_TRANSFER.BANK_IBAN', { iban: eInvoiceInfo['e_invoice.bank_iban'] }));
        }

        if (eInvoiceInfo['e_invoice.bic']) {
            bankTailRows.push(this.translateService.instant('CASHREGISTER.PAYMENTS.BANK_TRANSFER.BANK_BIC', { bic: eInvoiceInfo['e_invoice.bic'] }));
        }

        if(bankTailRows.length) {
            bankTailRows.unshift(
                this.translateService.instant('CASHREGISTER.PAYMENTS.BANK_TRANSFER.TAIL_TITLE'),
                this.translateService.instant('CASHREGISTER.PAYMENTS.BANK_TRANSFER.TAIL_DESCRIPTION'),
            );
        }

        return stringToLines(bankTailRows, 40).join("\n");
    }

    /**
     * Processes the sale change. Sets the change and the change type to the sale.
     *
     * @param {Sales} sale - The sale object.
     */
    public processSaleChange (sale: Sales) {
        sale.change_type = undefined;
        sale.change = undefined;
        sale.payments = sale.payments || [];

        const change = this.getSaleChange(sale) || 0;        

        if(!change) {
            return;
        }

        const cashPaymentsSum = sale.payments.filter(payment => this.fiscalUtils.isCashPayment(payment.payment_method_type_id)).reduce((sum, payment) => sum + payment.amount, 0);
        const ticketPaymentsSum = sale.payments.filter(payment => payment.payment_method_type_id === 6).reduce((sum, payment) => sum + payment.amount, 0);

        let changeType: Sales.ChangeTypeEnum;

        //Determine change type
        if(cashPaymentsSum > 0) {
            changeType = "cash";
        } else if(ticketPaymentsSum > 0) {
            changeType = "ticket";
        } else {
            changeType = "other";
        }

        let changeToGive = change;

        for(const payment of sale.payments) {
            switch(payment.payment_method_type_id) {
                case 19:
                case 21:
                    try {
                        let paymentMeta = JSON.parse(payment.payment_data || "{}");

                        if(paymentMeta.changeGiven) {
                            changeToGive = MathUtils.round(changeToGive - paymentMeta.changeGiven);
                        }
                    } catch(e) {}
                break;
                default:
                break;
            }
        }

        //Set change
        sale.change = changeToGive;
        sale.change_type = changeType;
    }

    /**
     * Converts quick coupons to tickets for a given sale.
     * This method removes all quick coupons from the sale object and adds them to the payments as tickets.
     *
     * @param {Sales} sale - The sale object.
     */
    public async quickCouponsToTickets(sale: Sales) {
        const quickCoupons = this.saleUtils.getConfiguredQuickCoupons();

        if(!quickCoupons.length) {
            return;
        }

        const saleQuickCoupons = removeFromArray(sale.sale_items || [], (saleItem) => 
            saleItem.type === 'coupon' &&
            saleItem.quantity === -1 &&
            quickCoupons.some((coupon) => coupon.name === saleItem.name && coupon.value === saleItem.price && coupon.department_id === saleItem.department_id)
        );

        if(!saleQuickCoupons.length) {
            return;
        }

        const ticketMethodType = paymentMethodTypes.find((paymentMethodType) => paymentMethodType.id === 6)!;
        const ticketPaymentMethods = await this.entityManagerService.paymentMethods.fetchCollectionOffline({ payment_method_type_id: 6 });

        if(!ticketPaymentMethods.length) {
            throw 'CASHREGISTER.PAYMENTS.NO_TICKET_CONFIGURED';
        }

        const ticketMethod = ticketPaymentMethods[0];

        const quickCouponsTickets = <SalesPayments[]>saleQuickCoupons.map((coupon) => ({
            amount: coupon.price,
            date: new Date().toISOString() as any,
            payment_method_id: ticketMethod.id,
            payment_method_name: ticketMethod.name,
            payment_method_type_id: ticketMethodType.id,
            payment_method_type_name: ticketMethodType.name,
            ticket_name: coupon.name,
            unclaimed: this.fiscalUtils.isMethodUnclaimed(ticketMethodType.id, ticketMethod.unclaimed)
        }));

        sale.payments = [...(sale.payments || []), ...quickCouponsTickets].filter((payment) => payment.amount !== 0);

        this.saleUtils.calculateSalePrices(sale);
    }
}